import {
    SUPPORTED_LOGICAL_OPERATORS,
    SUPPORTED_COMPARISONS_OPERATORS,
    COMPARISON_OPERATORS_SUPPORTED_VALUES_LENGTH
} from "../../constants/operations.js";

import { getValueFromNestedObject, parseJSON, isPlainObject } from "./helper.js";

/**
 * condition work similar to the mongodb aggregation operator. Please see them for reference
 * @see {@link https://www.mongodb.com/docs/manual/reference/operator/aggregation/}
 * @example { $and: [ { $eq: ["atlas_search.enabled", false] }, { $ne: ["atlas_search.search_fields.JOB.length", 0] }] }
 * In above condition
 * $eq: check if `atlas_search.enabled` is equal to `false`
 * $ne: check if the `atlas_search.search_fields.JOB.length` is not equal to 0
 * $and: it is logical operator for both the expressions
 * Terminology in code
 * $eq,$ne,others are mentioned as `comparison operators`
 * $and, $or are mentioned as `logical operators`
 * ["atlas_search.enabled", false] are mentioned as `expressions`. First value will be src for the data, second is usually the value to be compared to
 * { $and: [ { $eq: ["atlas_search.enabled", false] }, { $ne: ["atlas_search.search_fields.JOB.length", 0] }] } are mentioned as `condition`
 * @see {@link https://www.w3schools.com/js/js_comparisons.asp}
 */

/**
 * @exports evaluateCondition Evaluate the given condition against the data
 * @param {object} data Source data through which condition is validated Eg: {  atlas_search: { search_fields: {  JOB: ["job_description"], }, enabled: true } }
 * @param {object} condition Condition to check the provided data. Eg: { $and: [ { $eq: ["atlas_search.enabled", true] }, { $ne: ["atlas_search.search_fields.JOB.length", 0] }] }
 * @param {Object} optional Optional arguments
 * @param {boolean | false} optional.strict Runs the condition check type for both condition and data, throws error when something fails
 * @param {boolean | false} optional.debug_mode Return unsanitized error message. For development purpose
 * @param {boolean | false} optional.skip_validation Skips the validation when the strict is set to `true`
 * @param { 'number' | 'string' | 'object' | 'boolean' | false } optional.return_type Type check the result of condition
 * @returns { { type:'success' | 'error' , message?: string, data?:boolean }}
 * @example
 * evaluateCondition({  atlas_search: { search_fields: {  JOB: ["job_description"], }, enabled: true } }, { $and: [ { $eq: ["atlas_search.enabled", true] }, { $ne: ["atlas_search.search_fields.JOB.length", 0] }] } )
 * => { type: 'success' , data: true }
 */
export default function evaluateCondition(
    data,
    condition,
    { strict = true, debug_mode = false, skip_validation = false, return_type = 'boolean' } = {}
) {
    const sanitized_err_msg = "Oops! Something went wrong!";
    try {
        const error_msg =
            "evaluateCondition() Failed to run the condition on the provided data";

        const parsedData = parseJSON(data, { strict: false });

        // Skip validation when its not strict and skip validation is true
        if (strict || !skip_validation) {
            // Validating arguments
            if (typeof parsedData !== "object")
                throw new Error(error_msg, {
                    cause: `TYPE_ERROR: Invalid arguments. 'data' must be an object, Passed value: ${JSON.stringify(data)} type: ${typeof data}`,
                });
            validateCondition(condition, { strict });
        }

        // Process the condition and returns a boolean value
        const result = processCondition(data, condition);
        // Value hasn't been set through the condition
        if (result === null) {
            if (strict)
                throw new Error(error_msg, {
                    cause: "APP_ERR: Failed to set any value for the condition",
                });
        }
        if (return_type && typeof result !== return_type)
            if (strict)
                throw new Error(error_msg, {
                    cause: `APP_ERR: result for the condition is not in boolean. Result:'${result}' typeof result:'${typeof result}'`,
                });
        return {
            type: "success",
            data: result,
        };
    } catch (err) {
        console.error(err);
        if (strict) throw err;
        const error_msg = debug_mode
            ? err?.cause
                ? `${err.toString()} CAUSE: ${err.cause}`
                : "UNCAUGHT_ERROR: " + err.toString()
            : sanitized_err_msg;

        return {
            type: "error",
            message: error_msg,
        };
    }
}

/**
 * @function validateCondition Validate the condition, check if typeof condition and operators
 * @param {any} condition Condition to check the provided data.
 * @param {Object} optional
 * @param {boolean | true } optional.strict
 * @param {string[]} optional.append_operators Append new operators on the condition, so the function doesn't throw error on those keys
 * @returns {void}
 */
function validateCondition(
    condition,
    { strict = true, append_operators = [] } = {}
) {
    const error_msg = `validateCondition() Condition: '${JSON.stringify(
        condition
    )}' failed at validation, please check the condition`;

    if (typeof condition !== "object") {
        throw new Error(error_msg, {
            cause: `TYPE_ERROR: Condition can only be of type object. Passed type: ${typeof condition}`,
        });
    }

    const ALLOWED_OPERATORS = [
        ...SUPPORTED_LOGICAL_OPERATORS,
        ...SUPPORTED_COMPARISONS_OPERATORS,
        ...append_operators,
    ];
    const operators = Object.keys(condition);
    operators.forEach((key) => {
        if (!ALLOWED_OPERATORS.includes(key)) {
            throw new Error(error_msg, {
                cause: `UNKNOWN_OPERATOR: '${key}' is not a valid operator, only following operators are supported ${ALLOWED_OPERATORS.map(
                    (key) => `'${key}'`
                )}`,
            });
        }
    });

    // Loop through the operators and check if expressions are in correct type
    for (let op_index = 0; op_index < operators.length; op_index++) {
        const exp = condition[operators[op_index]];
        if (!Array.isArray(exp)) {
            throw new Error(error_msg, {
                cause: `TYPE_ERROR: Operator '${operators[op_index]}' value needs to be an array. Passed type: '${typeof operators[op_index]}'`,
            });
        } else if (exp.length === 0) {
            throw new Error(error_msg, {
                cause: `TYPE_ERROR: Operator '${operators[op_index]}' doesn't have any expression inside the array`,
            });
        }
        // For logical operator
        if (isOperatorLogical(operators[op_index])) {
            // Logical operations contains multiple comparison condition, 
            // so we're looping through them and validating each condition
            for (let log_index = 0; log_index < exp.length; log_index++) {
                validateCondition(condition[operators[op_index]][log_index]);
            }
        } else {
            for (let exp_index = 0; exp_index < exp.length; exp_index++) {
                /**
                 * expressions may contain condition inside them to which help retrieve value from array of objects eg: 
                 * { "$exists": [{ $elemMatch: ['_external:module', 'module.module_key', 'SETTINGS'] }] }
                 */
                if (isPlainObject(exp[exp_index])) {
                    validateCondition(exp[exp_index]);
                }
            }
        }
        // Check if no of expression passed for comparison operators
        if (SUPPORTED_COMPARISONS_OPERATORS.includes(operators[op_index])) {
            const supported_length = COMPARISON_OPERATORS_SUPPORTED_VALUES_LENGTH[operators[op_index]];
            if (exp.length != supported_length) {
                throw new Error(error_msg, {
                    cause: `TYPE_ERROR: ${supported_length} expression(s) are mandatory for '${operators[op_index]}'. Passed ${exp.length} expression(s)`,
                });
            }
        }
        // Conditional checks
        if (operators[op_index] === '$in') {
            // Type checking second operator is an array
            if (!Array.isArray(exp[1])) {
                throw new Error(error_msg, {
                    cause: `TYPE_ERROR: Operator '${operators[op_index]}' value needs to be an array. Invalid:${JSON.stringify(exp[1])}`,
                });
            }
        }
    }

    if (strict) {
        // Need to do some strict validation
    }
}

/**
 * @function processCondition Process the condition against the given data and return the boolean value
 * @param {Object} data Source data through which condition is validated
 * @param {Object} condition Condition to check the provided data.
 * @param {boolean[]} condition_results NOTE: Don't pass any value for this argument
 * @returns {boolean}
 */
function processCondition(data, condition, condition_results = []) {
    // Stores the result from all the conditions
    let result = null;
    const operators = Object.keys(condition);
    // Loop through the operators
    for (let op_index = 0; op_index < operators.length; op_index++) {
        const exp = condition[operators[op_index]];
        // If the operator is for logical, it contains list of condition, looping through them
        if (isOperatorLogical(operators[op_index])) {
            // Logical operations contains multiple comparison condition
            // so we're looping each condition and result of each condition is stored in `condition_results` array
            for (let log_index = 0; log_index < exp.length; log_index++) {
                processCondition(
                    data,
                    condition[operators[op_index]][log_index],
                    condition_results
                );
            }
            // Doing logical operations, 
            // results from `condition_results` are used as input for the logical operation
            result = operations_fn[operators[op_index]](condition_results);
        } else {
            const expression_values = [];
            // eg: $eq: ["atlas_search.enabled",true]. If
            for (let expIndex = 0; expIndex < exp.length; expIndex++) {
                const currentExp = exp[expIndex];
                // Expression may inturn have a condition
                // Eg: [{ $elemMatch: ['_external:module', 'module.module_key', 'SETTINGS'] }]
                if (isPlainObject(exp[expIndex])) {
                    const exp_result = processCondition(data, currentExp, []);
                    expression_values.push(exp_result);
                } else {
                    // Let us know if value needs to retrieved from data or exp is literal
                    // Eg: ["@path:popup_banner.type","@literal:POPUP"]. If the path and literals are not provided, first will take as path and second as comparison value
                    // Here first one `popup_banner.type` is the path that needs to be compared with literal "POPUP"
                    let isExpLiteral = true;

                    if ((typeof currentExp === 'string' && currentExp.includes('@literal'))) isExpLiteral = true;
                    else if ((typeof currentExp === 'string' && currentExp.includes('@path')) || expIndex === 0) isExpLiteral = false;

                    const sanitized_exp = typeof currentExp === 'string' ? currentExp.replaceAll('@path:', '').replaceAll('@literal:', '').replaceAll(':', '.') : currentExp;
                    if (isExpLiteral) expression_values.push(sanitized_exp);
                    else {
                        // retrieving value from nested object only for first expression
                        const retrievedValue = getValueFromNestedObject(data, sanitized_exp, { strict: false });
                        expression_values.push(retrievedValue != undefined ? retrievedValue : sanitized_exp);
                    }
                }
            }
            // Doing comparison operations
            result = operations_fn[operators[op_index]](...expression_values);
            condition_results.push(result);
        }
    }
    return result;
}
/**
 * @function isOperatorLogical Check if the operator is logical or not
 * @param {string} operator
 * @param {Object} optional
 * @param {string[]} optional.logical_operators Override supported logical operators
 * @param {string[]} optional.comparison_operators Override supported comparison operators
 * @returns {boolean}
 */
function isOperatorLogical(
    operator,
    {
        logical_operators = SUPPORTED_LOGICAL_OPERATORS,
        comparison_operators = SUPPORTED_COMPARISONS_OPERATORS,
    } = {}
) {
    if (comparison_operators.includes(operator)) return false;
    if (logical_operators.includes(operator)) return true;
    throw new Error(
        "isOperatorLogical() Failed to check the if the operator is logical",
        {
            cause: `Unsupported operator found: ${operator}.`,
        }
    );
}

const operations_fn = {
    // Comparison operations
    $eq: (value_1, value_2) => value_1 === value_2,
    $ne: (value_1, value_2) => value_1 !== value_2,
    $gte: (value_1, value_2) => value_1 >= value_2,
    $lte: (value_1, value_2) => value_1 <= value_2,
    $gt: (value_1, value_2) => value_1 > value_2,
    $lt: (value_1, value_2) => value_1 < value_2,
    $in: (src, values) => values.reduce((acc, curr) => acc && src?.includes(curr)),
    // Logical operations
    $and: (values) => values.reduce((acc, curr) => acc && !!curr),
    $or: (values) => values.reduce((acc, curr) => acc || !!curr),
    $exists(value) {
        if (Array.isArray(value)) return value.length !== 0;
        else if (typeof value === 'object') return isPlainObject(value);
        else if (typeof value === 'number') return !isNaN(value);
        else return !!value;
    },
    $notExists(value) {
        return !this.$exists(value);
    },
    // Getting nested object 
    $elemMatch: (arr, identifier, matchValue) => {
        if (!Array.isArray(arr)) throw new Error(`Data for $elemMatch is not an array. INVALID_VALUE:${JSON.stringify(arr)}`);
        return arr.find(a => {
            if (typeof a === 'object') {
                return getValueFromNestedObject(a, identifier, { strict: false }) === matchValue;
            } else {
                return a === matchValue;
            }
        });
    }
    // Arithmetic operations
};
